import { useState } from 'react';
import { toast } from 'sonner';
import { admin, apiErrorMessage, type AdminLibraryItem } from '../../lib/api';
import { useApi } from '../../hooks/useApi';
import { useTranslation } from '../../i18n';
import { DataTable, Button, Modal, type Column } from '../../components/ui';
import { useAdminT } from './adminI18n';

export default function AdminCurationPage() {
  const t = useAdminT();
  const { t: gt } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);
  const [bookId, setBookId] = useState('');
  const [category, setCategory] = useState('');
  const [gradeBand, setGradeBand] = useState('');
  const [sortWeight, setSortWeight] = useState('0');
  const [tags, setTags] = useState('');
  const [featured, setFeatured] = useState(false);
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [removeId, setRemoveId] = useState<string | null>(null);

  const list = useApi(() => admin.library(), [refreshKey]);
  const rows = list.status === 'success' ? list.data : [];

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!bookId.trim()) { toast.error(t('curation.bookId')); return; }
    setBusy(true);
    try {
      await admin.curate({
        book_id: bookId.trim(),
        category: category.trim() || null,
        grade_band: gradeBand.trim() || null,
        sort_weight: Number(sortWeight) || 0,
        tags: tags.split(',').map((s) => s.trim()).filter(Boolean),
        featured,
        rationale: rationale.trim() || null,
      });
      toast.success(t('curation.added'));
      setBookId(''); setCategory(''); setGradeBand(''); setSortWeight('0');
      setTags(''); setFeatured(false); setRationale('');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, gt));
    } finally {
      setBusy(false);
    }
  }

  async function confirmRemove() {
    if (!removeId) return;
    try {
      await admin.uncurate(removeId);
      toast.success(t('curation.removed'));
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(apiErrorMessage(err, gt));
    } finally {
      setRemoveId(null);
    }
  }

  const columns: Column<AdminLibraryItem>[] = [
    { key: 'title', header: t('col.book'), render: (r) => r.title ?? r.book_id.slice(0, 8) },
    { key: 'category', header: t('col.category'), width: '120px', render: (r) => r.category ?? '—' },
    { key: 'featured', header: t('col.featured'), width: '80px', render: (r) => (r.featured ? '★' : '—') },
    { key: 'sort_weight', header: t('col.weight'), width: '80px', align: 'right', mono: true,
      render: (r) => String(r.sort_weight ?? 0) },
    { key: 'actions', header: t('col.actions'), width: '90px', align: 'right',
      render: (r) => (
        <Button size="sm" variant="link" onClick={() => setRemoveId(r.book_id)}>
          {t('action.remove')}
        </Button>
      ) },
  ];

  return (
    <div className="admin-page">
      <header className="admin-page__head">
        <h1>{t('curation.title')}</h1>
      </header>

      <form onSubmit={add} className="admin-curate-form">
        <h2 className="admin-form__title">{t('curation.formTitle')}</h2>
        <div className="admin-form__grid">
          <label className="admin-field">
            <span className="admin-field__label">{t('curation.bookId')}</span>
            <input className="prop-choice__text" value={bookId} onChange={(e) => setBookId(e.target.value)} />
          </label>
          <label className="admin-field">
            <span className="admin-field__label">{t('curation.category')}</span>
            <input className="prop-choice__text" value={category} onChange={(e) => setCategory(e.target.value)} />
          </label>
          <label className="admin-field">
            <span className="admin-field__label">{t('curation.gradeBand')}</span>
            <input className="prop-choice__text" value={gradeBand} onChange={(e) => setGradeBand(e.target.value)} />
          </label>
          <label className="admin-field">
            <span className="admin-field__label">{t('curation.sortWeight')}</span>
            <input className="prop-choice__text" type="number" value={sortWeight}
              onChange={(e) => setSortWeight(e.target.value)} />
          </label>
          <label className="admin-field">
            <span className="admin-field__label">{t('curation.tags')}</span>
            <input className="prop-choice__text" value={tags} onChange={(e) => setTags(e.target.value)} />
          </label>
          <label className="admin-field admin-field--checkbox">
            <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} />
            <span>{t('curation.featured')}</span>
          </label>
        </div>
        <label className="admin-field">
          <span className="admin-field__label">{t('curation.rationale')}</span>
          <textarea className="prop-textarea" rows={2} value={rationale}
            onChange={(e) => setRationale(e.target.value)} />
        </label>
        <Button type="submit" variant="accent" disabled={busy}>{t('action.add')}</Button>
      </form>

      <section className="admin-section">
        <div className="sec-head">
          <h2>{t('curation.listTitle')}</h2>
          <span className="meta">{rows.length}</span>
        </div>
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.book_id}
          loading={list.status === 'loading'} empty={t('emptyGeneric')} />
      </section>

      <Modal
        open={removeId != null}
        onOpenChange={(o) => { if (!o) setRemoveId(null); }}
        title={t('action.remove')}
        description={t('curation.removeConfirm')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setRemoveId(null)}>{t('action.cancel')}</Button>
            <Button variant="danger" size="sm" onClick={confirmRemove}>{t('action.remove')}</Button>
          </>
        }
      />
    </div>
  );
}
